import { useState } from "react";
import { Alert, Box, Button, Chip, Divider, MenuItem, Stack, Switch, TextField, Typography,  } from "@mui/material";

import { Tooltip } from "@lopie/kernel-ui";

/**
 * When a connector should run (LO-73).
 *
 * Config in the kernel, not a cadence baked into a Crossplane composition —
 * otherwise "sync hourly instead of daily" is a deploy, and a console that
 * cannot change a schedule without one has not replaced anything.
 *
 * **Two cadences, not one.** A frequent delta keeps the graph current; a rare
 * full reconcile is what makes deletion detectable. One "sync every N" field
 * would force them to share, and running a full enumeration as often as a
 * delta pays for a full pull to learn nothing almost every time.
 *
 * The options offered come from the floors the API publishes, so this file
 * holds no copy of them. It could not enforce them anyway — the next caller
 * is an API client, not this page — which is why the kernel refuses a cadence
 * under the floor rather than trusting a dropdown.
 */

function optionsFrom(min, candidates) {
  return candidates.filter((n) => n >= min);
}

/** "1 day", not "1 days". */
function plural(n, unit) {
  return `${n} ${unit}${n === 1 ? "" : "s"}`;
}

export default function Schedule({ source, schedule, onSave }) {
  const [enabled, setEnabled] = useState(Boolean(schedule?.enabled));
  const [delta, setDelta] = useState(schedule?.delta_minutes ?? "");
  const [full, setFull] = useState(schedule?.full_hours ?? "");
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState(null);
  const [saved, setSaved] = useState(false);

  const minDelta = schedule?.min_delta_minutes ?? 15;
  const minFull = schedule?.min_full_hours ?? 6;

  const deltaOptions = optionsFrom(minDelta, [15, 30, 60, 180, 360, 720]);
  const fullOptions = optionsFrom(minFull, [6, 12, 24, 72, 168]);

  const dirty =
    enabled !== Boolean(schedule?.enabled) ||
    String(delta) !== String(schedule?.delta_minutes ?? "") ||
    String(full) !== String(schedule?.full_hours ?? "");

  async function save() {
    setSaving(true);
    setFailure(null);
    setSaved(false);
    try {
      await onSave({
        enabled,
        delta_minutes: delta === "" ? null : Number(delta),
        full_hours: full === "" ? null : Number(full),
      });
      setSaved(true);
    } catch (e) {
      setFailure(e);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Divider sx={{ mb: 1.5 }} />
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "center", mb: 1 }}
      >
        <Typography variant="overline" color="text.secondary">
          Schedule
        </Typography>
        <Tooltip title="Nothing runs these yet. Setting a schedule records what you want; the thing that acts on it is not built.">
          <Chip size="small" label="not yet firing" variant="outlined"
                sx={{ color: "kernel.attention", borderColor: "kernel.attention" }} />
        </Tooltip>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
          <Switch
            size="small"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            // v9: the input slot, not `inputProps` — which is forwarded to
            // the DOM verbatim and lands as `inputprops="[object Object]"`,
            // taking the aria-label with it. React says so in development
            // only, which is how it survived three deploys.
            slotProps={{ input: { "aria-label": `Schedule ${source}` } }}
          />
          <Typography variant="body2">{enabled ? "On" : "Off"}</Typography>
        </Stack>

        <TextField
          select
          size="small"
          label="Delta every"
          value={delta}
          onChange={(e) => setDelta(e.target.value)}
          sx={{ minWidth: 150 }}
          disabled={!enabled}
        >
          <MenuItem value="">never</MenuItem>
          {deltaOptions.map((n) => (
            <MenuItem key={n} value={n}>
              {n < 60 ? plural(n, "minute") : plural(n / 60, "hour")}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Full reconcile every"
          value={full}
          onChange={(e) => setFull(e.target.value)}
          sx={{ minWidth: 180 }}
          disabled={!enabled}
        >
          <MenuItem value="">never</MenuItem>
          {fullOptions.map((n) => (
            <MenuItem key={n} value={n}>
              {n < 24 ? plural(n, "hour") : plural(n / 24, "day")}
            </MenuItem>
          ))}
        </TextField>

        <Button
          size="small"
          variant="contained"
          disabled={!dirty || saving}
          onClick={save}
        >
          {saving ? "Saving…" : "Save"}
        </Button>

        {saved && !dirty ? (
          <Typography variant="body2" sx={{ color: "kernel.permitted" }}>
            Saved
          </Typography>
        ) : null}
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
        Minimum {minDelta} minutes for a delta and {minFull} hours for a full
        reconcile, enforced by the kernel rather than by this page — a faster
        cadence is how a source starts rate-limiting us.
      </Typography>

      {failure ? (
        <Alert severity="error" variant="outlined" sx={{ mt: 1 }}>
          {failure.message}
        </Alert>
      ) : null}
    </>
  );
}
